import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, ChatCircleText, EnvelopeSimple, Broadcast, ArrowSquareOut, Tray, Crosshair, DownloadSimple, FilmSlate, ArrowClockwise } from "@phosphor-icons/react";
import client from "../../lib/api";
import { pollJob } from "./WinWallReelCard";
import MetaAudienceCard from "./MetaAudienceCard";
import { CopyBtn } from "./CopyBtn";

const money = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const BASE = "https://freightpilotai.ai";

export const TEMPLATES = {
  creator: {
    label: "Creator DM (TikTok / IG / YouTube)",
    icon: ChatCircleText,
    build: (p = {}) => `Hey${p.name ? ` ${p.name.split(" ")[0]}` : ""} — I'm an owner-op getting my MC right now and I built a free tool your audience asks about every video: it checks brokers for scams in 3 seconds and tracks your authority till FMCSA flips it ACTIVE (texts you the day it happens).

Want to do a paid video? Flat fee + ${money(p.payout_cents ?? 5000)} for every paying customer from your link, paid monthly. You get your own promo code (${p.promo_code || "50% off first month"}) and a live stats page so you see every click.

${p.link ? `Your link would be ${p.link}` : `Details: ${BASE}/partners`} — and I'll give you a free account so you can actually use it on camera.`,
  },
  agent: {
    label: "Insurance agent / BOC-3 / factoring email",
    icon: EnvelopeSimple,
    build: (p = {}) => `Subject: Something to hand your new-authority clients

Hi${p.name ? ` ${p.name.split(" ")[0]}` : ""},

Every new carrier you set up asks the same thing after the MC is filed: "what do I do next?" I built a free checklist for exactly that — it tracks their MC on FMCSA and texts them the day it goes ACTIVE, checks brokers for scams and double-brokering, and sends their carrier packet to brokers.

If you share it with your clients, you get ${money(p.payout_cents ?? 7500)} for every one who becomes a paying customer (paid monthly), plus your own promo code${p.promo_code ? ` (${p.promo_code})` : ""} that gives them 50% off their first month. Your link${p.link ? `: ${p.link}` : ` and stats page come the same day you say yes.`}

It makes you look good, costs you nothing, and I'm an owner-operator myself — happy to jump on a 10-minute call.

Mike · FreightPilot AI · ${BASE}/partners`,
  },
};


function PixelBox() {
  const [ids, setIds] = useState({ tiktok_pixel_id: "", meta_pixel_id: "" });
  const [saved, setSaved] = useState({ tiktok_pixel_id: "", meta_pixel_id: "" });
  const [capi, setCapi] = useState(null);
  useEffect(() => {
    client.get("/public/pixels").then((r) => { const v = { tiktok_pixel_id: r.data.tiktok_pixel_id || "", meta_pixel_id: r.data.meta_pixel_id || "" }; setIds(v); setSaved(v); }).catch(() => {});
    client.get("/admin/pixels/capi-status").then((r) => setCapi(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const { data } = await client.put("/admin/pixels", { tiktok_pixel_id: ids.tiktok_pixel_id.trim(), meta_pixel_id: ids.meta_pixel_id.trim() });
      setSaved(data); setIds(data);
      toast.success("Pixels saved — live on the next page load");
    } catch (err) { setIds(saved); toast.error(err?.response?.status === 422 ? "Meta pixel = digits only, TikTok pixel = letters and numbers — not saved" : err?.response?.data?.detail || "Couldn't save"); }
  };
  const field = (key, label, placeholder, hint, testid) => (
    <div className="mb-3">
      <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-bold mb-1">{label}</label>
      <input value={ids[key]} onChange={(e) => setIds((s) => ({ ...s, [key]: e.target.value }))} placeholder={placeholder} className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-white placeholder:text-white/25 focus:outline-none focus:border-[#FF5A1F]" data-testid={`pixel-${testid}-input`} />
      <div className="text-[11px] mt-1" data-testid={`pixel-${testid}-status`}>{saved[key] ? <span className="text-emerald-300">● {label} {saved[key]} is live</span> : <span className="text-neutral-500">○ {label} not set</span>} <span className="text-neutral-600">· {hint}</span></div>
    </div>
  );
  return (
    <div className="card-surface p-5" data-testid="pixel-box">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 font-bold mb-2"><Broadcast size={14} /> Ad pixels · retargeting</div>
      <p className="text-xs text-neutral-400 mb-3">Both pixels fire PageView on every page plus CompleteRegistration / Subscribe. Change an ID here and it goes live on the next page load — no redeploy. Google tag is set separately in the site build.</p>
      {field("meta_pixel_id", "Meta Pixel ID", "1556270059225623", "Events Manager → Data sources → your pixel → Settings → Dataset ID", "meta")}
      {field("tiktok_pixel_id", "TikTok Pixel ID", "C1A2B3C4D5E6F7G8H9", "TikTok Ads Manager → Assets → Events → Web Events", "tiktok")}
      {capi && (
        <div className={`text-[11px] mb-3 rounded-md border px-3 py-2 ${capi.configured ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-200" : "border-amber-500/30 bg-amber-500/[0.06] text-amber-200"}`} data-testid="pixel-capi-status">
          {capi.configured
            ? `● Server-side Meta events ON — signup, card added, trial start and first payment are also sent from the server (pixel ${capi.pixel_id}), so iPhone ad-blockers can't hide them.`
            : "○ Server-side Meta events OFF — add the Conversions API access token (Events Manager → your pixel → Settings → Conversions API → Generate access token) as META_CAPI_ACCESS_TOKEN in the deployment secrets. Everything else is already wired."}
        </div>
      )}
      <button type="button" onClick={save} className="btn-primary rounded-full px-4 py-2 text-sm" data-testid="pixel-save">Save pixels</button>
    </div>
  );
}

function Applications() {
  const [rows, setRows] = useState(null);
  useEffect(() => { client.get("/admin/partner-applications").then((r) => setRows(r.data.applications)).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="card-surface p-5" data-testid="partner-applications">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 font-bold mb-2"><Tray size={14} /> Applications from /partners · {rows.length}</div>
      <ul className="space-y-1.5 text-sm">
        {rows.slice(0, 8).map((a) => (
          <li key={a.id} className="flex flex-wrap gap-x-3 text-neutral-300" data-testid={`partner-application-${a.id}`}>
            <b className="text-white">{a.name}</b><span className="text-neutral-500">{a.type}</span><a href={`mailto:${a.email}`} className="text-[#FF5A1F]">{a.email}</a>
            {a.handle && <span className="text-neutral-400">{a.handle}</span>}{a.audience && <span className="text-neutral-500 truncate max-w-[40ch]">— {a.audience}</span>}
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-neutral-500 mt-2">Create their partner above with the same email — the welcome email with link + promo goes out automatically.</p>
    </div>
  );
}

const RETARGET_URL = `${BASE}/authority-checklist?utm_source=tiktok&utm_medium=paid&utm_campaign=retarget_checklist`;
const RETARGET_AUDIENCE = `Audience name: Checklist visitors — no signup (30d)
Source: Website Traffic → Pixel "FreightPilotAI" (DAG2V2JC77UDHLL3NM6G)
Include: event ViewContent · content_name equals "checklist_view" · last 30 days
Exclude: event CompleteRegistration · last 30 days
Exclude: event Subscribe · last 180 days`;
const RETARGET_ADS = [
  { label: "Proof", text: `They were waiting on their MC three weeks ago. Now they're booked.\n\nThe free New Authority checklist tracks your MC on FMCSA and texts you the day it flips ACTIVE. Then it helps you send your carrier packet to brokers.\n\nYou already opened it — finish it. ${RETARGET_URL}` },
  { label: "Direct", text: `Still waiting on your MC? Stop refreshing SAFER.\n\nThe checklist checks FMCSA for you and texts you the day you go ACTIVE — then hands you the 7-day first-load plan.\n\n7-day free trial. ${RETARGET_URL}` },
  { label: "Fear-of-loss", text: `Every day your authority sits ACTIVE without a load is money gone.\n\nCarriers on the checklist are booking their first load in under a week — BrokerCheck, packet sent, rate checked.\n\nPick up where you left off: ${RETARGET_URL}` },
];

function RetargetAdVideo() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { client.get("/admin/retarget/ad-video").then((r) => setSt(r.data)).catch(() => setSt({ latest: null, wall_wins: 0 })); }, []);
  const render = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/admin/retarget/ad-video/render?force=${st?.latest ? "true" : "false"}`);
      const result = await pollJob(data.job_id);
      setSt((s) => ({ ...s, latest: result }));
      toast.success(result.cached ? "Ad video is up to date" : `Ad video rendered — ${result.duration_sec}s${result.voiceover ? ", voiced" : ""}`);
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Render failed"); }
    finally { setBusy(false); }
  };
  if (!st) return null;
  const r = st.latest;
  const src = r ? (r.cdn_url || `${process.env.REACT_APP_BACKEND_URL}${r.mp4_url}`) : null;
  return (
    <div className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-4" data-testid="retarget-ad-video">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-white"><FilmSlate size={15} className="text-emerald-400" /> Video ad · ~9s · voiced</div>
        <button type="button" onClick={render} disabled={busy} className="btn-primary rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="retarget-ad-video-render">
          <ArrowClockwise size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Rendering…" : r ? "Re-render" : "Render video ad"}
        </button>
      </div>
      <p className="text-[11px] text-neutral-500 mb-2">{st.wall_wins ? "Newest Win Wall card" : "Sample card (no wins yet)"} with a slow push-in, onyx voiceover, then the CTA. Rates hidden. Upload as a Spark/in-feed ad to the audience above.</p>
      {r && (
        <div className="flex flex-col sm:flex-row gap-4" data-testid="retarget-ad-video-latest">
          <video src={src} controls playsInline className="w-32 rounded-lg border border-white/10 bg-black aspect-[9/16]" data-testid="retarget-ad-video-player" />
          <div className="flex-1 min-w-0 text-xs space-y-2">
            <div className="text-neutral-400" data-testid="retarget-ad-video-meta">{r.duration_sec}s · {r.voiceover ? `voiceover (${r.voice})` : "silent"} · {r.sample ? "sample card" : "real win"} · rendered {new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
            <div className="text-neutral-300 whitespace-pre-wrap" data-testid="retarget-ad-video-copy">{r.copy?.primary}</div>
            <div className="flex flex-wrap gap-2">
              <a href={src} download="freightpilot-retarget-ad.mp4" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs font-bold text-white/80 hover:bg-white/[0.06]" data-testid="retarget-ad-video-download"><DownloadSimple size={12} /> Download MP4</a>
              <CopyBtn text={r.copy?.primary || ""} label="Copy ad text" testid="retarget-ad-video-copy-text" />
              <CopyBtn text={r.copy?.headline || ""} label="Copy headline" testid="retarget-ad-video-copy-headline" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DemoAdFormat({ fmt, label, hint, latest, onRendered }) {
  const [busy, setBusy] = useState(false);
  const render = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/admin/retarget/demo-video/render?format=${fmt}&force=${latest ? "true" : "false"}`);
      const result = await pollJob(data.job_id);
      onRendered(result);
      toast.success(result.cached ? `${label} demo ad is up to date` : `${label} demo ad rendered — ${result.duration_sec}s${result.voiceover ? ", voiced" : ""}`);
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Render failed"); }
    finally { setBusy(false); }
  };
  const r = latest;
  const src = r ? (r.cdn_url || `${process.env.REACT_APP_BACKEND_URL}${r.mp4_url}`) : null;
  return (
    <div className="mt-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3" data-testid={`demo-ad-video-${fmt}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
        <div className="text-xs font-semibold text-white">{label} · {hint}</div>
        <button type="button" onClick={render} disabled={busy} className="btn-primary rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`demo-ad-video-render-${fmt}`}>
          <ArrowClockwise size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Rendering (1–3 min)…" : r ? "Re-render" : `Render ${label}`}
        </button>
      </div>
      {r && (
        <div className="flex flex-col sm:flex-row gap-4" data-testid={`demo-ad-video-latest-${fmt}`}>
          <video src={src} controls playsInline className={`${fmt === "square" ? "w-40 aspect-square" : "w-32 aspect-[9/16]"} rounded-lg border border-white/10 bg-black`} data-testid={`demo-ad-video-player-${fmt}`} />
          <div className="flex-1 min-w-0 text-xs space-y-2">
            <div className="text-neutral-400" data-testid={`demo-ad-video-meta-${fmt}`}>{r.duration_sec}s · {r.width || 1080}×{r.height || 1920} · {r.scenes} scenes · {r.voiceover ? `voiceover (${r.voice})` : "silent"} · rendered {new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
            <div className="flex flex-wrap gap-2">
              <a href={src} download={`freightpilot-demo-ad-60s-${fmt}.mp4`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs font-bold text-white/80 hover:bg-white/[0.06]" data-testid={`demo-ad-video-download-${fmt}`}><DownloadSimple size={12} /> Download MP4</a>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${r.srt_url}`} download={`freightpilot-demo-ad-60s-${fmt}.srt`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs font-bold text-white/80 hover:bg-white/[0.06]" data-testid={`demo-ad-video-download-srt-${fmt}`}><DownloadSimple size={12} /> Captions (SRT)</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DemoAdVideo() {
  const [st, setSt] = useState(null);
  useEffect(() => { client.get("/admin/retarget/demo-video").then((r) => setSt(r.data)).catch(() => setSt({ latest: null, latest_square: null })); }, []);
  if (!st) return null;
  const copy = (st.latest || st.latest_square || st.latest_clip)?.copy;
  return (
    <div className="mt-4 rounded-lg border border-sky-500/20 bg-sky-500/[0.04] p-4" data-testid="demo-ad-video">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-white mb-1"><FilmSlate size={15} className="text-sky-400" /> Facebook demo ad · 60s · real screens · voiced</div>
      <p className="text-[11px] text-neutral-500 mb-1">Hook → BrokerCheck → MC status → packet open alert → payroll → IFTA miles → vault → 7-day trial CTA. Burned-in captions + SRT. Upload the MP4 in Meta's "Browse media → Upload", then add the SRT under Captions. Give Meta both sizes: 9:16 fills Stories/Reels, 1:1 fills the feed on desktop.</p>
      <DemoAdFormat fmt="story" label="9:16" hint="Stories · Reels · mobile feed" latest={st.latest} onRendered={(r) => setSt((s) => ({ ...s, latest: r }))} />
      <DemoAdFormat fmt="square" label="1:1" hint="Facebook feed · desktop · Instagram feed" latest={st.latest_square} onRendered={(r) => setSt((s) => ({ ...s, latest_square: r }))} />
      <DemoAdFormat fmt="clip" label="1:1 · 12s silent" hint="Pricing page hero clip (auto-plays muted, loops)" latest={st.latest_clip} onRendered={(r) => setSt((s) => ({ ...s, latest_clip: r }))} />
      {copy && (
        <div className="mt-3 text-xs space-y-2">
          <div className="text-neutral-300 whitespace-pre-wrap line-clamp-4" data-testid="demo-ad-video-copy">{copy.primary}</div>
          <div className="flex flex-wrap gap-2">
            <CopyBtn text={copy.primary || ""} label="Copy ad text" testid="demo-ad-video-copy-text" />
            <CopyBtn text={copy.headline || ""} label="Copy headline" testid="demo-ad-video-copy-headline" />
          </div>
        </div>
      )}
    </div>
  );
}

function RetargetKit() {
  const [tab, setTab] = useState("story");
  const img = `${process.env.REACT_APP_BACKEND_URL}/api/public/retarget/ad-card.png?size=${tab}`;
  return (
    <div className="card-surface p-5 lg:col-span-2" data-testid="retarget-kit">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 font-bold"><Crosshair size={14} /> TikTok retargeting · checklist visitors who didn't sign up</div>
        <a href="https://ads.tiktok.com/i18n/audiences" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#FF5A1F] hover:underline" data-testid="retarget-open-tiktok"><ArrowSquareOut size={12} /> Open TikTok Audiences</a>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4" data-testid="retarget-step-audience">
          <div className="flex items-center justify-between gap-2 mb-2"><div className="text-sm font-semibold text-white">1 · Create the audience</div><CopyBtn text={RETARGET_AUDIENCE} label="Copy rules" testid="retarget-copy-audience" /></div>
          <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-neutral-400 font-sans">{RETARGET_AUDIENCE}</pre>
          <p className="text-[11px] text-neutral-500 mt-2">Assets → Audiences → Create → Custom Audience → Website Traffic. The checklist page fires <code className="text-neutral-300">ViewContent</code> on every visit; signups fire <code className="text-neutral-300">CompleteRegistration</code>. Needs ~1,000 people before TikTok lets you target it.</p>
        </div>
        <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4" data-testid="retarget-step-creative">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-white">2 · Creative</div>
            <div className="flex gap-1 text-[11px]">
              {[["story", "9:16"], ["feed", "4:5"]].map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`rounded-full px-2 py-0.5 border ${tab === k ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-white/15 text-neutral-400"}`} data-testid={`retarget-creative-${k}`}>{l}</button>)}
            </div>
          </div>
          <img src={img} alt="Retargeting ad creative — newest first-load win card" className="w-full max-w-[180px] rounded-lg border border-white/10 mx-auto" data-testid="retarget-creative-image" />
          <a href={img} download={`freightpilot-retarget-${tab}.png`} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs font-bold text-white/80 hover:bg-white/[0.06]" data-testid="retarget-creative-download"><DownloadSimple size={12} /> Download PNG</a>
          <p className="text-[11px] text-neutral-500 mt-2">Newest Win Wall card, rate hidden. Video version below.</p>
        </div>
        <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4" data-testid="retarget-step-copy">
          <div className="text-sm font-semibold text-white mb-2">3 · Ad text + landing link</div>
          <div className="space-y-2">
            {RETARGET_ADS.map((a) => (
              <div key={a.label} className="flex items-start justify-between gap-2 text-[12px]" data-testid={`retarget-ad-${a.label.toLowerCase()}`}>
                <div className="text-neutral-400 leading-relaxed whitespace-pre-wrap line-clamp-3">{a.text}</div>
                <CopyBtn text={a.text} label={a.label} testid={`retarget-copy-ad-${a.label.toLowerCase()}`} />
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2"><code className="text-[11px] text-neutral-400 truncate">{RETARGET_URL}</code><CopyBtn text={RETARGET_URL} label="Link" testid="retarget-copy-url" /></div>
          <p className="text-[11px] text-neutral-500 mt-2">Objective: Traffic or Lead Generation · optimize for CompleteRegistration · $10–20/day is plenty for a warm audience this size.</p>
        </div>
      </div>
      <RetargetAdVideo />
      <DemoAdVideo />
      <MetaAudienceCard />
    </div>
  );
}

export function PartnerKit() {
  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-8" data-testid="partner-kit">
      <div className="card-surface p-5 lg:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Outreach kit</div>
          <a href={`${BASE}/partners`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#FF5A1F] hover:underline" data-testid="partner-kit-pitch-link"><ArrowSquareOut size={12} /> One-page pitch: freightpilotai.ai/partners</a>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(TEMPLATES).map(([k, t]) => (
            <div key={k} className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4" data-testid={`partner-template-${k}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-white"><t.icon size={15} /> {t.label}</div>
                <CopyBtn text={t.build()} label="Copy" testid={`partner-template-copy-${k}`} />
              </div>
              <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-neutral-400 font-sans max-h-52 overflow-y-auto">{t.build()}</pre>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-neutral-500 mt-3">Each partner row has a "Copy DM" that fills in their name, link, promo code and payout.</p>
      </div>
      <PixelBox />
      <Applications />
      <RetargetKit />
    </div>
  );
}
